<script setup lang="ts">
import { PlusIcon, TrashIcon, BadgeCheckIcon, ExclamationIcon, ChevronRightIcon } from '@heroicons/vue/outline';
import { watchDebounced } from '@vueuse/core';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  RadioGroup,
  RadioGroupLabel,
  RadioGroupOption,
} from '@headlessui/vue';
import { getApiRoot } from '~/utilities/api';

const props = defineProps({
  mintType: { type: String, required: false, default: undefined },
  metadata: { type: Object, required: true },
  onChainMetadata: { type: Object, required: true },
  licenseFromCollection: { type: Boolean, required: true },
});

const { metadata, onChainMetadata, licenseFromCollection, mintType } = toRefs(props);

const fetchingLicenseInProgress = ref<boolean>(false);
const licenseError = ref<string | undefined>(undefined);
watchDebounced(
  () => onChainMetadata.value.licenseUrl,
  async (licenseUrl) => {
    if (licenseFromCollection.value) {
      return;
    }

    if (licenseUrl && licenseUrl.startsWith('http')) {
      fetchingLicenseInProgress.value = true;
      try {
        const { hash } = await $fetch<{ hash: string }>(`${getApiRoot()}/studio/calculate_hash`, {
          method: 'post',
          body: { url: licenseUrl },
        });
        onChainMetadata.value.licenseHash = hash;
        licenseError.value = undefined;
        return;
      } catch (error: any) {
        licenseError.value = error.error?.message;
      } finally {
        fetchingLicenseInProgress.value = false;
      }
    } else {
      licenseError.value = undefined;
    }
    onChainMetadata.value.licenseHash = null;
  },
  { debounce: 300 }
);

const dataString = ref('');
watch(
  () => metadata.value.data,
  () => {
    const stringifiedData = JSON.stringify(metadata.value.data, null, 2);
    if (dataString.value !== stringifiedData) {
      dataString.value = stringifiedData;
    }
  }
);
watch(metadata, () => {
  // do a reset of the data string when the metadata changes due to a reset
  dataString.value = '';
});

const isValidJson = ref<boolean | undefined>(undefined);
watchDebounced(
  dataString,
  (data) => {
    if (!data?.trim()) {
      metadata.value.data = undefined;
      isValidJson.value = undefined;
      return;
    }
    try {
      metadata.value.data = JSON.parse(data);
      isValidJson.value = true;
    } catch (error) {
      isValidJson.value = false;
    }
  },
  { debounce: 1000 }
);

const newAttribute = reactive({ trait_type: '', value: '' });
const addAttribute = () => {
  const existingName = metadata.value?.attributes.find(({ trait_type }) => trait_type === newAttribute.trait_type);
  if (metadata.value?.attributes && !existingName) {
    metadata.value?.attributes.push({ trait_type: newAttribute.trait_type, value: newAttribute.value });
    newAttribute.trait_type = '';
    newAttribute.value = '';
  }
};

const deleteAttribute = (traitTypeToDelete: string) => {
  const existingAttributeIndex = metadata.value?.attributes.findIndex(
    ({ trait_type }) => trait_type === traitTypeToDelete
  );
  if (existingAttributeIndex >= 0) {
    metadata.value?.attributes.splice(existingAttributeIndex, 1);
  }
};

const royaltyOptions = [
  { name: '5%', value: 5 },
  { name: '10%', value: 10 },
  { name: '15%', value: 15 },
  { name: 'Custom' },
];

const selectedRoyaltyOption = ref<{ name: string; value?: number } | undefined>(undefined);
watch(selectedRoyaltyOption, (option) => {
  if (option?.name === 'Custom') {
    onChainMetadata.value.royaltyPercentage = 0;
  } else if (option?.value) {
    onChainMetadata.value.royaltyPercentage = option.value;
  }
});

watch(onChainMetadata, () => {
  // do a reset of the radio group when the onChainMetadata changes due to a reset
  selectedRoyaltyOption.value = undefined;
  resetRadioGroup.value = true;
});

// The radio group doesn't seem to reset when setting the value to undefined
// This enforces a reset of the radio group by removing it from the DOM
// It usually happens while the user is on a different screen, so it's not visible
const resetRadioGroup = ref(false);
watch(resetRadioGroup, () => {
  if (resetRadioGroup.value) {
    setTimeout(() => {
      resetRadioGroup.value = false;
    });
  }
});
</script>
<template>
  <template v-if="mintType !== 'series'">
    <div>
      <label for="name" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200"> NFT Name </label>
      <div class="mt-1">
        <input
          id="name"
          v-model="metadata.name"
          type="text"
          name="name"
          autocomplete="name"
          required
          class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
        />
      </div>
    </div>

    <div>
      <label for="description" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
        NFT Description
      </label>
      <div class="mt-1">
        <textarea
          id="description"
          v-model="metadata.description"
          name="description"
          rows="5"
          class="block w-full rounded-md border border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
        />
      </div>
    </div>
  </template>

  <div>
    <label for="website" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200"> License URL </label>
    <div class="relative mt-1">
      <input
        id="licenseUrl"
        v-model="onChainMetadata.licenseUrl"
        type="text"
        name="licenseUrl"
        autocomplete="licenseUrl"
        :disabled="licenseFromCollection"
        :class="licenseFromCollection ? 'cursor-not-allowed bg-neutral-100 dark:bg-neutral-900' : ''"
        class="block w-full rounded-md border-neutral-300 pr-10 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
      />
      <div
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center border-neutral-300 pr-3 dark:border-neutral-600"
      >
        <svg
          v-if="fetchingLicenseInProgress"
          class="h-5 w-5 animate-spin text-emerald-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <BadgeCheckIcon
          v-else-if="onChainMetadata.licenseUrl && onChainMetadata.licenseHash"
          class="h-5 w-5 text-emerald-600"
        />
        <ExclamationIcon
          v-else-if="onChainMetadata.licenseUrl && onChainMetadata.licenseHash === null"
          class="h-5 w-5 text-yellow-600"
        />
      </div>
    </div>
    <p class="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
      By assigning a license to the NFT, buyers of your NFTs will directly see what rights they will receive when owning
      your NFTs. Learn more on
      <NuxtLink
        class="font-medium text-emerald-600"
        to="https://docs.mintgarden.io/mintgarden-studio/mint-an-nft#license-url"
        target="_blank"
        >docs.mintgarden.io
      </NuxtLink>
      .
    </p>
    <p v-if="licenseError" id="license-error" class="mt-2 text-sm text-red-600">{{ licenseError }}</p>
  </div>

  <Disclosure v-if="mintType !== 'series'" v-slot="{ open }">
    <DisclosureButton class="flex items-center gap-1">
      <span class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">Advanced Settings</span>
      <ChevronRightIcon class="h-4 w-4" :class="open && 'rotate-90 transform'" />
    </DisclosureButton>
    <DisclosurePanel class="mx-4 flex flex-col gap-6">
      <div>
        <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">Attributes</label>
        <div v-for="attribute in metadata.attributes" :key="attribute.trait_type" class="mt-3 flex gap-3">
          <div class="flex-grow">
            <input
              id="attribute-trait_type"
              type="text"
              :value="attribute.trait_type"
              disabled
              name="attribute-trait_type"
              class="block w-full cursor-not-allowed rounded-md border-neutral-300 bg-neutral-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 sm:text-sm"
            />
          </div>
          <div class="flex-grow">
            <input
              id="attribute-value"
              type="text"
              :value="attribute.value"
              disabled
              name="attribute-value"
              class="block w-full cursor-not-allowed rounded-md border-neutral-300 bg-neutral-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 sm:text-sm"
            />
          </div>
          <span>
            <button
              type="button"
              class="inline-flex items-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:border-neutral-600 dark:bg-black dark:text-neutral-200 dark:hover:bg-neutral-900"
              @click="deleteAttribute(attribute.trait_type)"
            >
              <TrashIcon class="-ml-2 mr-1 h-5 w-5 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
              <span>Delete</span>
            </button>
          </span>
        </div>
        <form class="mt-3 flex gap-3" @submit.prevent="addAttribute">
          <div class="flex-grow">
            <input
              id="new-attribute-trait_type"
              v-model="newAttribute.trait_type"
              type="text"
              name="new-attribute-trait_type"
              class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
              placeholder="name / trait_type"
            />
          </div>
          <div class="flex-grow">
            <input
              id="new-attribute-value"
              v-model="newAttribute.value"
              type="text"
              name="new-attribute-value"
              class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
              placeholder="value"
            />
          </div>
          <span>
            <button
              type="submit"
              class="inline-flex items-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:border-neutral-600 dark:bg-black dark:text-neutral-200 dark:hover:bg-neutral-900"
            >
              <PlusIcon class="-ml-2 mr-1 h-5 w-5 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
              <span>Add</span>
            </button>
          </span>
        </form>
      </div>

      <div>
        <label for="data" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
          Additional data
        </label>
        <div class="relative mt-1">
          <textarea
            id="data"
            v-model="dataString"
            name="data"
            placeholder="This should contain valid JSON.
It will be added to the `data` field in the NFT metadata."
            rows="5"
            class="block w-full rounded-md border border-neutral-300 font-mono shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
          />
          <div
            class="pointer-events-none absolute right-0 top-0 flex items-center border-neutral-300 pr-3 pt-3 dark:border-neutral-600"
          >
            <BadgeCheckIcon v-if="isValidJson === true" class="h-5 w-5 text-emerald-600" />
            <ExclamationIcon v-else-if="isValidJson === false" class="h-5 w-5 text-yellow-600" />
          </div>
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>
  <div>
    <label for="name" class="block text-sm font-medium text-neutral-700 dark:text-neutral-200">
      Royalty percentage
    </label>
    <RadioGroup v-if="!resetRadioGroup" v-model="selectedRoyaltyOption" class="mt-2">
      <RadioGroupLabel class="sr-only">Choose a royalty percentage</RadioGroupLabel>
      <div class="grid grid-cols-4 gap-3">
        <RadioGroupOption
          v-for="option in royaltyOptions"
          :key="option.name"
          v-slot="{ active, checked }"
          as="template"
          :value="option"
        >
          <div
            :class="[
              active ? 'ring-2 ring-emerald-600 ring-offset-2' : '',
              checked
                ? 'bg-emerald-600 text-white hover:bg-emerald-500 dark:text-black'
                : 'bg-white text-neutral-700 ring-1 ring-inset ring-neutral-300 hover:bg-neutral-50 dark:bg-black dark:text-neutral-200 dark:ring-neutral-600 dark:hover:bg-neutral-900',
              'flex cursor-pointer items-center justify-center rounded-md px-3 py-3 text-sm font-semibold sm:flex-1',
            ]"
          >
            <RadioGroupLabel as="span">{{ option.name }}</RadioGroupLabel>
          </div>
        </RadioGroupOption>
      </div>
    </RadioGroup>
    <div v-if="selectedRoyaltyOption?.name === 'Custom'" class="relative mt-3">
      <div
        class="pointer-events-none absolute inset-y-0 left-0 flex items-center border-neutral-300 pl-3 text-neutral-500 dark:text-neutral-400 sm:text-sm"
      >
        %
      </div>
      <input
        id="royaltyPercentage"
        v-model="onChainMetadata.royaltyPercentage"
        type="number"
        step="any"
        name="royaltyPercentage"
        min="0"
        max="100"
        class="block w-full rounded-md border-neutral-300 pl-10 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white sm:text-sm"
      />
    </div>
  </div>
</template>
